import React from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Path } from 'react-native-svg';
import { colors } from '../theme/tokens';
import { styleMiroir } from '../i18n/sens';

type Variant = 'mescv' | 'linkedin' | 'entretien' | 'notecv' | 'generatecv' | 'paywall' | 'cvhub' | 'cvhub-left';

// Positionnement flexible : chaque variant choisit ses ancres (top/bottom + left/right).
// Les décors de coin haut-droit gardent top+right ; les décors latéraux utilisent left/bottom.
interface Shape {
  width: number;
  height: number;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  d: string;
}

// Chaque écran a sa silhouette : même technique (dégradé vertical violet, sans contour),
// mais une forme/position différente pour ne pas être identique.
const SHAPES: Record<Variant, Shape> = {
  mescv: { width: 320, height: 300, top: -50, right: -50, d: 'M 100 0 C 105 150 185 215 320 200 L 320 0 Z' },
  linkedin: { width: 360, height: 320, top: -40, right: -80, d: 'M 40 0 C 30 200 150 250 360 250 L 360 0 Z' },
  entretien: { width: 340, height: 300, top: -60, right: -70, d: 'M 70 0 C 60 160 160 205 340 215 L 340 0 Z' },
  notecv: { width: 380, height: 300, top: -50, right: -90, d: 'M 50 0 C 40 140 170 200 380 190 L 380 0 Z' },
  generatecv: { width: 300, height: 340, top: -80, right: -50, d: 'M 120 0 C 135 210 195 245 300 230 L 300 0 Z' },
  paywall: { width: 360, height: 280, top: -40, right: -60, d: 'M 90 0 C 80 130 150 185 360 175 L 360 0 Z' },
  cvhub: { width: 330, height: 300, top: -50, right: -60, d: 'M 90 0 C 95 155 175 210 330 205 L 330 0 Z' },
  // Décor latéral milieu-gauche : déborde hors de l'écran à gauche, ancré ~milieu vertical.
  'cvhub-left': { width: 300, height: 320, top: 300, left: -140, d: 'M 0 0 C 155 5 215 90 205 320 L 0 320 Z' },
};

// Décor du coin haut-droit : forme arrondie qui déborde hors de l'écran (haut + droite).
// Remplissage = dégradé VERTICAL violet uniquement (présent en haut → transparent en bas), sans contour.
export function DecorBlob({ variant = 'mescv' }: { variant?: Variant } = {}) {
  const shape = SHAPES[variant];
  const gradientId = `blobFill-${variant}`;
  return (
    <Svg
      width={shape.width}
      height={shape.height}
      viewBox={`0 0 ${shape.width} ${shape.height}`}
      // Logique : en droite à gauche, le décor passe au coin opposé, son tracé retourné avec lui.
      style={[styles.svg, { top: shape.top, bottom: shape.bottom, start: shape.left, end: shape.right }, styleMiroir()]}
      pointerEvents="none"
    >
      <Defs>
        <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <Stop offset="0" stopColor={colors.violet} stopOpacity={0.45} />
          <Stop offset="0.6" stopColor={colors.violet} stopOpacity={0.28} />
          <Stop offset="1" stopColor={colors.violet} stopOpacity={0} />
        </LinearGradient>
      </Defs>
      <Path d={shape.d} fill={`url(#${gradientId})`} />
    </Svg>
  );
}

const styles = StyleSheet.create({
  svg: { position: 'absolute' },
});
